/**
 * Student Services Landing Page
 * Value-added services: Education Loan, Forex, Housing, Insurance
 */
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Banknote,
  RefreshCw,
  Home,
  Shield,
  ChevronRight,
  CheckCircle,
  Phone,
  MessageCircle,
  ArrowRight
} from 'lucide-react';

const API_URL = window.location.origin;

// Icon mapping for CMS-driven services
const iconMap = {
  Banknote, RefreshCw, Home, Shield
};

// Fallback services (used if CMS empty)
const fallbackServices = {
  'education-loan': {
    title: 'Education Loan',
    tagline: 'Fund Your Dreams',
    icon: 'Banknote',
    color: 'from-emerald-500 to-teal-600',
    description: 'Get hassle-free education loans with competitive interest rates from our partner banks.',
    features: [
      'Loans up to ₹1.5 Crore',
      'Interest rates from 8.5% p.a.',
      'No collateral up to ₹7.5 Lakhs',
      'Quick processing in 7-10 days',
      'Moratorium period available',
      'Tax benefits under Section 80E'
    ],
    partners: ['HDFC Credila', 'Avanse', 'ICICI Bank', 'Axis Bank', 'Prodigy Finance'],
    cta: 'Check Loan Eligibility'
  },
  'forex': {
    title: 'Forex Services',
    tagline: 'Best Exchange Rates',
    icon: 'RefreshCw',
    color: 'from-blue-500 to-indigo-600',
    description: 'Get the best foreign exchange rates for your tuition fees and living expenses.',
    features: [
      'Competitive exchange rates',
      'Zero hidden charges',
      'Same-day transfers available',
      'Forex card with multi-currency support',
      'Wire transfers for tuition payment',
      'Real-time rate tracking'
    ],
    partners: ['BookMyForex', 'Thomas Cook', 'Centrum', 'ICICI Bank'],
    cta: 'Get Today\'s Rate'
  },
  'housing': {
    title: 'Student Housing',
    tagline: 'Your Home Away From Home',
    icon: 'Home',
    color: 'from-orange-500 to-amber-600',
    description: 'Find safe, affordable, and convenient accommodation near your university.',
    features: [
      'Verified accommodations',
      'On-campus & off-campus options',
      'Shared & private rooms',
      'All-inclusive rent options',
      'Virtual tours available',
      '24/7 support'
    ],
    partners: ['University Living', 'Amber', 'UniAcco', 'Student.com'],
    cta: 'Browse Accommodations'
  },
  'insurance': {
    title: 'Health Insurance',
    tagline: 'Stay Protected Abroad',
    icon: 'Shield',
    color: 'from-violet-500 to-purple-600',
    description: 'Comprehensive health insurance plans designed specifically for international students.',
    features: [
      'Covers medical emergencies',
      'Visa-compliant policies',
      'Outpatient & inpatient coverage',
      'Mental health support included',
      'Repatriation coverage',
      '24/7 global assistance'
    ],
    partners: ['OSHC Australia', 'UHC Student', 'ISO Insurance', 'Guard.me'],
    cta: 'Get Insurance Quote'
  }
};

const StudentServicesPage = () => {
  const { isDark } = useTheme();
  const { serviceType } = useParams();
  const { content: cmsContent } = usePageContent('student-services');

  // Read CMS content with fallbacks
  const heroContent = getSection(cmsContent, 'hero', {
    badge: 'Student Essentials',
    title: 'Everything You Need for Your Journey',
    subtitle: "From financing your education to settling abroad, we've got you covered with our comprehensive student services."
  });

  const helpCta = getSection(cmsContent, 'help_cta', {
    title: 'Need Help Choosing?',
    subtitle: 'Our counselors can help you understand which services you need based on your destination and budget.',
    call_label: 'Call Us Now',
    whatsapp_label: 'WhatsApp'
  });

  const cmsServicesArr = getSection(cmsContent, 'student_services_list', null);
  // Build services map: CMS array (keyed by slug) → object, OR fallback
  const services = (Array.isArray(cmsServicesArr) && cmsServicesArr.length > 0)
    ? cmsServicesArr.reduce((acc, s) => {
        if (s.slug) acc[s.slug] = s;
        return acc;
      }, {})
    : fallbackServices;

  // Resolve icon component
  const resolveIcon = (name) => iconMap[name] || Banknote;
  
  // If no specific service, show overview
  if (!serviceType) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`} data-testid="student-services-overview">
        <Header />
        
        {/* Hero */}
        <section className={`pt-28 pb-16 ${isDark ? 'bg-gradient-to-b from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-b from-white to-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="mb-4 bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
              {heroContent.badge}
            </Badge>
            <h1 className={`text-4xl md:text-5xl font-bold font-display mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {heroContent.title}
            </h1>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              {heroContent.subtitle}
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {Object.entries(services).map(([key, service]) => {
                const Icon = resolveIcon(service.icon);
                return (
                  <Link key={key} to={`/student-services/${key}`} className="group" data-testid={`service-card-${key}`}>
                    <Card className={`h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
                      isDark ? 'bg-[#1a2830] border-white/10 hover:border-ux-cyan/50' : 'hover:border-ux-cyan/50'
                    }`}>
                      <CardContent className="p-8">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${service.color} flex items-center justify-center mb-6`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <h3 className={`text-2xl font-bold font-display mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {service.title}
                        </h3>
                        <p className={`text-sm font-medium mb-3 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                          {service.tagline}
                        </p>
                        <p className={`mb-6 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          {service.description}
                        </p>
                        <div className="flex items-center gap-2 text-ux-cyan font-medium group-hover:gap-3 transition-all">
                          Learn More <ArrowRight className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-2xl md:text-3xl font-bold font-display mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="help-cta-title">
              {helpCta.title}
            </h2>
            <p className={`mb-8 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              {helpCta.subtitle}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white rounded-full px-8" asChild>
                <a href="tel:+919639557799">
                  <Phone className="w-5 h-5 mr-2" />
                  {helpCta.call_label}
                </a>
              </Button>
              <Button size="lg" variant={isDark ? 'hero-outline' : 'outline'} className="rounded-full px-8" asChild>
                <a href="https://wa.me/919639557799">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {helpCta.whatsapp_label}
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Specific service page
  const service = services[serviceType];
  
  if (!service) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`}>
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Service Not Found</h1>
          <Button className="mt-4" asChild>
            <Link to="/student-services">View All Services</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = resolveIcon(service.icon);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`}>
      <Header />
      
      {/* Hero */}
      <section className={`pt-28 pb-16 ${isDark ? 'bg-gradient-to-b from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-b from-white to-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Link to="/student-services" className={`inline-flex items-center text-sm mb-6 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'} hover:underline`}>
              ← Back to Student Essentials
            </Link>
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${service.color} flex items-center justify-center mb-6`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <Badge className={`mb-4 bg-gradient-to-r ${service.color} text-white`}>
              {service.tagline}
            </Badge>
            <h1 className={`text-4xl md:text-5xl font-bold font-display mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {service.title}
            </h1>
            <p className={`text-lg ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className={`text-2xl font-bold font-display mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What We Offer
              </h2>
              <div className="space-y-4">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className={`w-5 h-5 mt-0.5 ${isDark ? 'text-ux-green' : 'text-green-500'}`} />
                    <span className={isDark ? 'text-white/80' : 'text-slate-700'}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h2 className={`text-2xl font-bold font-display mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Our Partners
              </h2>
              <div className="flex flex-wrap gap-3">
                {service.partners.map((partner, idx) => (
                  <Badge key={idx} variant="outline" className={`text-sm py-2 px-4 ${isDark ? 'border-white/20 text-white' : ''}`}>
                    {partner}
                  </Badge>
                ))}
              </div>
              
              <Card className={`mt-8 ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
                <CardContent className="p-6">
                  <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Get Started Today
                  </h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    Speak with our experts to understand your options and get personalized recommendations.
                  </p>
                  <div className="space-y-3">
                    <Button className={`w-full bg-gradient-to-r ${service.color} text-white`} asChild>
                      <a href="https://wa.me/919639557799">
                        {service.cta}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                    <Button variant="outline" className={`w-full ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`} asChild>
                      <a href="tel:+919639557799">
                        <Phone className="w-4 h-4 mr-2" />
                        Talk to an Expert
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudentServicesPage;
